import { useModal } from "@/app/shared/modal-views/use-modal";
import { EndPoints } from "@/Core/Client";
import useEventCalendar from "@/hooks/use-event-calendar";
import { CalendarEvent } from "@/types";
import { DatePicker } from "@/ui/datepicker";
import { Form } from "@/ui/form";
import { Controller, SubmitHandler } from "react-hook-form";
import toast from "react-hot-toast";
import { PiXBold } from "react-icons/pi";
import { ActionIcon, Button, Input, Select, Text, Textarea, Title } from "rizzui";
import ReferToSelect from "@/app/shared/refer-to-select";
import { EventFormDto, eventFormSchema } from "@/Dtos/event-dto";
import FileUploadField from "@/app/shared/file-upload-field";
import { EEventTicketType, EEventType } from "@/Core/enums";
import { useMutation, useQueryClient } from "@tanstack/react-query";

interface CreateEventProps {
    startDate?: Date;
    endDate?: Date;
    event?: CalendarEvent;
}

const eventTypeOptions = Object.values(EEventType).map((value) => ({ label: value, value }));
const ticketTypeOptions = Object.values(EEventTicketType).map((value) => ({ label: value, value }));

export default function EventForm({
    startDate,
    endDate,
    event,
}: CreateEventProps) {
    const { closeModal } = useModal();
    const queryClient = useQueryClient();
    const { createEvent } = useEventCalendar();

    const mutation = useMutation({
        mutationFn: createEvent, // TODO: updateEvent
        onSuccess: () => {
            queryClient.refetchQueries({ queryKey: ["events"] });
            toast.success(
                <Text as="b">
                    Event saved successfully
                </Text>,
            );

            closeModal();
        }
    });

    const isUpdateEvent = event !== undefined;

    const onSubmit: SubmitHandler<EventFormDto> = (data) => {
        const isNewEvent = data.id === "" || data.id === undefined;

        if (isNewEvent) {
            mutation.mutate(data);
        } else {
            toast.error("Operation not supported");
        }
    };

    return (
        <div className="m-auto p-4 md:px-7 md:py-10">
            <div className="mb-6 flex items-center justify-between">
                <Title as="h3" className="text-lg">
                    {isUpdateEvent ? "Update Event" : "Create a new event"}
                </Title>
                <ActionIcon
                    size="sm"
                    variant="text"
                    onClick={() => closeModal()}
                    className="p-0 text-gray-500 hover:!text-gray-900"
                >
                    <PiXBold className="h-[18px] w-[18px]" />
                </ActionIcon>
            </div>

            <Form<EventFormDto>
                validationSchema={eventFormSchema}
                onSubmit={onSubmit}
                useFormProps={{
                    defaultValues: {
                        name: event?.title ?? "",
                        description: event?.description ?? "",
                        venueId: event?.location ?? "",
                        startDate: (startDate ?? event?.start),
                        endDate: (endDate ?? event?.end),
                    },
                }}
                className="grid grid-cols-1 gap-5 @container md:grid-cols-2 [&_label]:font-medium"
            >
                {({ register, setValue, control, watch, formState: { errors, isValid } }) => {
                    const startDate = watch("startDate");
                    const endDate = watch("endDate");
                    const ticketType = watch("ticketType");
                    return (
                        <>
                            <input
                                type="hidden"
                                {...register("id")}
                                value={event?.id}
                            />
                            <Input
                                label="Event Name"
                                placeholder="Enter a name of event"
                                {...register("name")}
                                className="col-span-full"
                                error={errors.name?.message}
                            />

                            <Controller
                                {...register("type")}
                                control={control}
                                //defaultValue={EEventType.Public}
                                render={({ field: { onChange, value } }) => (
                                    <Select
                                        dropdownClassName="h-auto"
                                        options={eventTypeOptions}
                                        value={value}
                                        onChange={onChange}
                                        label="Event Type"
                                        error={errors?.type?.message as string}
                                        getOptionValue={(option) => option.value}
                                        displayValue={(selected) =>
                                            eventTypeOptions.find(
                                                (o) => o.value === selected,
                                            )?.label
                                        }
                                    />
                                )}
                            />

                            <Controller
                                {...register("ticketType")}
                                control={control}
                                //defaultValue={EEventTicketType.Pay}
                                render={({ field: { onChange, value } }) => (
                                    <Select
                                        dropdownClassName="h-auto"
                                        options={ticketTypeOptions}
                                        value={value}
                                        onChange={onChange}
                                        label="Ticket Type"
                                        error={errors?.ticketType?.message as string}
                                        getOptionValue={(option) => option.value}
                                        displayValue={(selected) =>
                                            ticketTypeOptions.find(
                                                (o) => o.value === selected,
                                            )?.label
                                        }
                                    />
                                )}
                            />

                            <Input
                                label="Ticket Price"
                                placeholder="Enter ticket price"
                                {...register("price")}
                                error={errors.price?.message}
                                step="0.01" defaultValue={ticketType === EEventTicketType.Free ? "0" : ""}
                                readOnly={ticketType === EEventTicketType.Free}
                            />

                            <Input
                                label="Seats"
                                placeholder="Number of seats"
                                {...register("capacity")}
                                error={errors.capacity?.message}
                            />

                            <Textarea
                                label="Event Description"
                                placeholder="Enter your event description"
                                {...register("description")}
                                error={errors.description?.message}
                                textareaClassName="h-20"
                                className="col-span-full"
                            />

                            <ReferToSelect
                                title="Event Venue"
                                name="venueId"
                                register={register}
                                setValue={setValue}
                                defaultValue={""}
                                className="col-span-full"
                                error={errors.venueId?.message ?? ""}
                                referToEndPoint={EndPoints.venues.referTo}
                            />

                            <Controller
                                {...register("image")}
                                control={control}
                                render={({
                                    field: { onChange, onBlur },
                                    fieldState: { error },
                                }) => (
                                    <FileUploadField
                                        accept="img"
                                        name="image"
                                        className="col-span-full"
                                        onChange={(e: any) => onChange(e.target.files[0])}
                                        onBlur={onBlur}
                                        error={error?.message}
                                        label="Upload event image"
                                    />
                                )}
                            />

                            <Controller
                                name="startDate"
                                control={control}
                                render={({ field: { value, onChange } }) => (
                                    <DatePicker
                                        popperPlacement="top-start"
                                        selected={value}
                                        onChange={onChange}
                                        selectsStart
                                        startDate={value}
                                        endDate={endDate}
                                        minDate={new Date()}
                                        showTimeSelect
                                        dateFormat="MMMM d, yyyy h:mm aa"
                                        className="date-picker-event-calendar"
                                        placeholderText="Event Start Date"
                                        error={errors.venueId?.message ?? ""}
                                    />
                                )}
                            />
                            <Controller
                                name="endDate"
                                control={control}
                                render={({ field: { value, onChange } }) => (
                                    <DatePicker
                                        popperPlacement="top-start"
                                        selected={value}
                                        onChange={onChange}
                                        selectsEnd
                                        minDate={startDate}
                                        startDate={startDate}
                                        endDate={value}
                                        showTimeSelect
                                        dateFormat="MMMM d, yyyy h:mm aa"
                                        className="date-picker-event-calendar"
                                        placeholderText="Event End Date"
                                        error={errors.venueId?.message ?? ""}
                                    />
                                )}
                            />
                            <div className="col-span-full grid grid-cols-2 gap-4 pt-5">
                                <Button variant="outline" onClick={() => closeModal()}
                                    className="w-full @xl:w-auto dark:hover:border-gray-400">
                                    Cancel
                                </Button>
                                <Button type="submit" className="hover:gray-700 w-full @xl:w-auto" onClick={() => console.log('Is ', { isValid, errors })}>
                                    Save
                                </Button>
                            </div>
                        </>
                    );
                }}
            </Form>
        </div>
    );
}
